import React, { useState, useEffect, useCallback } from 'react';
import { Button, LazyDayPicker, Modal } from 'common/lazy';
import { useClassName } from 'common/hooks';
import { DateTime } from 'luxon';
import './operational-hours-modal.less';
import {
  DesktopOnly,
  getContentfulField,
  SVGIcon as Icon,
  MobileOnly,
  SVGIcon,
} from 'common/components';
import { parseRideMaintenanceSchedule } from 'public/components/AttractionScreen/RideClosures/utils';
import { request } from 'utils/api';
import { formatOpeningHoursForDate } from 'utils/helpers/openingHours';

const timeToAmPm = (time) => {
  return DateTime.fromFormat(time, 'HH:mm', {
    zone: 'America/New_York',
  }).toFormat('h:mm a');
};

const OperationalHoursModal = ({
  open,
  trigger,
  onOpen,
  onClose,
  initialDate,
  isDateUnavailable,
  updateSelectedDate,
  openingHoursBanner,
  openingHours,
  ridesCards = [],
  venueSlug,
  venueId,
  emptyAsset,
  emptyAssetStyle,
}) => {
  const classNames = useClassName('OperationalHoursModal');
  const [closedInfoVisible, setClosedInfoVisible] = useState(false);
  const [charactersAppearancesVisible, setCharactersAppearancesVisible] =
    useState(false);
  const [characterAppearances, setCharacterAppearances] = useState([]);
  const [calendarSelectedDate, setCalendarSelectedDate] = useState(() => {
    if (initialDate) {
      try {
        return DateTime.fromISO(initialDate).toJSDate();
      } catch (e) {
        return new Date();
      }
    }
    return new Date();
  });
  const toISO = (jsDate) => {
    if (!jsDate || !(jsDate instanceof Date)) return null;
    return DateTime.fromJSDate(jsDate).toISODate();
  };

  useEffect(() => {
    if (initialDate) {
      try {
        const newDate = DateTime.fromISO(initialDate).toJSDate();
        setCalendarSelectedDate(newDate);
      } catch (e) {
        console.error('Error parsing initialDate:', e);
      }
    }
  }, [initialDate]);

  useEffect(() => {
    if (!venueId || !calendarSelectedDate) return;
    const date = DateTime.fromJSDate(calendarSelectedDate).toISODate();
    request({
      method: 'GET',
      path: `/1/appearance?venueId=${venueId}&date=${date}`,
    })
      .then((res) => {
        setCharacterAppearances(res.data || []);
      })
      .catch((err) => console.error('Failed to fetch appearances:', err));
  }, [venueId, calendarSelectedDate]);

  const isDateBeforeToday = (date) => {
    if (!date || !(date instanceof Date)) return false;
    return new Date(date.toDateString()) < new Date(new Date().toDateString());
  };

  const onDayClick = (date) => {
    if (!date || !(date instanceof Date)) return;
    if (isDateBeforeToday(date)) return;

    const isoDate = toISO(date);
    if (isoDate && isDateUnavailable(isoDate)) return;

    setCalendarSelectedDate(date);
    updateSelectedDate(date);
  };

  const formatSelectedDateAsShort = (date) => {
    if (!date || !(date instanceof Date)) return '';

    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    };

    if (!date || !(date instanceof Date)) return '';
    const dt = DateTime.fromJSDate(date);
    const day = dt.day;
    const suffix = getOrdinalSuffix(day);
    return `${dt.toFormat('LLL d')}${suffix}`;
  };

  const formatSelectedDate = (date) => {
    if (!date || !(date instanceof Date)) return '';
    return DateTime.fromJSDate(date).toFormat('cccc, LLLL d, yyyy');
  };

  const getTemporaryClosedInfo = useCallback(() => {
    if (!calendarSelectedDate || !(calendarSelectedDate instanceof Date)) {
      return null;
    }
    const maintenanceSchedule = parseRideMaintenanceSchedule(
      ridesCards.map((ride) => ({
        ...ride,
        schedule: {
          ...ride.schedule,
          closedDates:
            ride.schedule?.closedDates?.map((closedDate) => ({
              ...closedDate,
              startDate: DateTime.fromISO(closedDate.startDate),
              endDate: DateTime.fromISO(closedDate.endDate),
              reason: closedDate.reason || '',
            })) || [],
        },
      }))
    );

    const selectedDateTime =
      DateTime.fromJSDate(calendarSelectedDate).startOf('day');

    const relevantItems = maintenanceSchedule
      .flatMap((maintenance) => maintenance.items)
      .filter((item) => {
        if (!item.startDate) {
          return true;
        }
        const startDate =
          item.startDate instanceof DateTime
            ? item.startDate
            : DateTime.fromISO(item.startDate);

        const endDate =
          item.endDate instanceof DateTime
            ? item.endDate
            : DateTime.fromISO(item.endDate);

        return selectedDateTime >= startDate && selectedDateTime <= endDate;
      });

    if (relevantItems.length === 0) {
      return null;
    }

    const message = relevantItems
      .map((item) => `• ${item.name.trim()}: ${item.reason.trim()}`)
      .join('\n');

    return {
      isClosed: true,
      message,
    };
  }, [ridesCards, calendarSelectedDate]);

  const closedInfo = getTemporaryClosedInfo();

  const useEmptyImage = !(
    characterAppearances?.length > 0 ||
    closedInfo ||
    openingHoursBanner
  );

  return (
    <Modal
      basic
      open={open}
      trigger={trigger}
      onOpen={onOpen}
      size="small"
      onClose={onClose}
      className={classNames('modal')}>
      <div className={classNames('container')}>
        <div className={classNames('inner')}>
          <h3 className={classNames('container__title')}>
            <span>Operational Hours</span>
            <button
              className={classNames('container__title__button')}
              onClick={onClose}>
              <SVGIcon
                name="close"
                className={classNames('container__title__button__icon')}
              />
            </button>
          </h3>
          <div className={classNames('container__content')}>
            {useEmptyImage && emptyAsset && (
              <img
                style={emptyAssetStyle}
                src={getContentfulField(emptyAsset.fields.file).url}
                alt={getContentfulField(emptyAsset.fields.file).title}
                className={classNames('container__content__hours__no-content')}
              />
            )}
            <div className={classNames('container__content__calendar')}>
              <LazyDayPicker
                className={`day-picker`}
                disabledDays={[
                  { before: DateTime.local().toJSDate() },
                  (jsDate) => {
                    if (!jsDate || !(jsDate instanceof Date)) return false;
                    const iso = toISO(jsDate);
                    return iso ? isDateUnavailable(iso) : false;
                  },
                ]}
                enableOutsideDaysClick={false}
                selectedDays={calendarSelectedDate}
                month={calendarSelectedDate || new Date()}
                onDayClick={(date) => onDayClick(date)}
              />
              <DesktopOnly style={{ marginTop: 'auto' }}>
                <Button
                  onClick={() => {
                    window.location.href = `/venue/${venueSlug}/tickets/${DateTime.fromJSDate(
                      calendarSelectedDate
                    ).toFormat('yyyy-MM-dd')}`;
                  }}
                  fluid
                  inverted
                  size="large"
                  className={classNames(
                    'container__content__calendar__button'
                  )}>
                  Get Tickets For{' '}
                  {formatSelectedDateAsShort(calendarSelectedDate)}
                </Button>
              </DesktopOnly>
            </div>

            <div className={classNames('container__content__hours')}>
              <p className={classNames('container__content__hours__date')}>
                {formatSelectedDate(calendarSelectedDate)}
              </p>
              <p className={classNames('container__content__hours__time')}>
                {formatOpeningHoursForDate(openingHours, calendarSelectedDate)}
              </p>
              {openingHoursBanner && openingHoursBanner?.asset && (
                <img
                  src={openingHoursBanner.asset.mobile.assetUrl}
                  alt={openingHoursBanner.asset.name}
                  className={classNames('container__content__hours__banner')}
                />
              )}
              {characterAppearances?.length > 0 && (
                <div
                  className={classNames(
                    'container__content__hours__characters-appearances'
                  )}>
                  <p
                    onClick={() => {
                      setCharactersAppearancesVisible(
                        !charactersAppearancesVisible
                      );
                    }}
                    className={classNames(
                      'container__content__hours__characters-appearances__title'
                    )}>
                    <Icon
                      name="drama"
                      className={classNames(
                        'container__content__hours__characters-appearances__title__icon'
                      )}
                    />
                    <span>Characters Appearances</span>
                    <Icon
                      name="angle-down"
                      className={classNames(
                        'container__content__hours__characters-appearances__title__arrow'
                      )}
                    />
                  </p>
                  {charactersAppearancesVisible && (
                    <div
                      className={classNames(
                        'container__content__hours__characters-appearances__block'
                      )}>
                      {Object.entries(
                        characterAppearances.reduce((acc, curr) => {
                          if (!acc[curr.startTime]) acc[curr.startTime] = [];
                          acc[curr.startTime].push(curr);
                          return acc;
                        }, {})
                      )
                        .sort(([timeA], [timeB]) => timeA.localeCompare(timeB))
                        .map(([time, items]) => (
                          <div
                            key={time}
                            className={classNames(
                              'container__content__hours__characters-appearances__block__item'
                            )}>
                            <p
                              className={classNames(
                                'container__content__hours__characters-appearances__block__item__date'
                              )}>
                              {timeToAmPm(time)}
                            </p>
                            <div
                              className={classNames(
                                'container__content__hours__characters-appearances__block__item__characters'
                              )}>
                              {items.map((item, idx) => (
                                <div
                                  key={`${item.character?.id}-${idx}`}
                                  className={classNames(
                                    'container__content__hours__characters-appearances__block__item__characters__item'
                                  )}>
                                  {item.character?.image && (
                                    <img
                                      src={item.character?.image}
                                      alt={item.character?.name}
                                      className={classNames(
                                        'container__content__hours__characters-appearances__block__item__characters__item__image'
                                      )}
                                    />
                                  )}
                                  <div
                                    className={classNames(
                                      'container__content__hours__characters-appearances__block__item__characters__item__info'
                                    )}>
                                    <p
                                      className={classNames(
                                        'container__content__hours__characters-appearances__block__item__characters__item__name'
                                      )}>
                                      {item.character?.name}
                                    </p>
                                    <p
                                      className={classNames(
                                        'container__content__hours__characters-appearances__block__item__characters__item__location'
                                      )}>
                                      <Icon
                                        name="location-pin"
                                        className={classNames(
                                          'container__content__hours__characters-appearances__block__item__characters__item__location__icon'
                                        )}
                                      />
                                      <span>{item.location?.name}</span>
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
              {closedInfo && (
                <div
                  className={classNames(
                    'container__content__hours__temporary-closed'
                  )}>
                  <p
                    onClick={() => {
                      setClosedInfoVisible(!closedInfoVisible);
                    }}
                    className={classNames(
                      'container__content__hours__temporary-closed__title'
                    )}>
                    <Icon
                      name="construction"
                      className={classNames(
                        'container__content__hours__temporary-closed__title__icon'
                      )}
                    />
                    <span>Temporary closures on this date</span>
                    <Icon
                      name={closedInfoVisible ? 'angle-up' : 'angle-down'}
                      className={classNames(
                        'container__content__hours__temporary-closed__title__arrow'
                      )}
                    />
                  </p>
                  {closedInfoVisible && (
                    <div
                      className={classNames(
                        'container__content__hours__temporary-closed__text'
                      )}>
                      {closedInfo.message.split('\n').map((line, index) => (
                        <p key={index}>{line || ' '}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <MobileOnly>
          <Button
            onClick={() => {
              window.location.href = `/venue/${venueSlug}/tickets/${DateTime.fromJSDate(
                calendarSelectedDate
              ).toFormat('yyyy-MM-dd')}`;
            }}
            fluid
            inverted
            size="large"
            className={classNames('container__content__calendar__button')}>
            Get Tickets For {formatSelectedDateAsShort(calendarSelectedDate)}
          </Button>
        </MobileOnly>
      </div>
    </Modal>
  );
};

export default OperationalHoursModal;
